/**
 * Placement Test History Page
 * Shows past test results with CEFR level timeline
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import placementTestService, {
    TestHistoryEntry,
    CEFRLevel,
} from '../services/placementTestService';
import './PlacementTestHistoryPage.css';

const CEFR_ORDER: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const getLevelColor = (level: CEFRLevel): string => {
    const colors: Record<CEFRLevel, string> = {
        A1: '#22c55e', A2: '#84cc16', B1: '#eab308',
        B2: '#f97316', C1: '#ef4444', C2: '#dc2626',
    };
    return colors[level];
};

const formatDate = (iso: string, locale: string): string => {
    const d = new Date(iso);
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
};

const PlacementTestHistoryPage: React.FC = () => {
    const { t, i18n } = useTranslation('placementTest');
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [entries, setEntries] = useState<TestHistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login?redirect=/placement-test/history');
            return;
        }

        const load = async () => {
            try {
                if (!localStorage.getItem('accessToken')) return;
                const data = await placementTestService.getHistory();
                setEntries(data.results);
            } catch {
                // silently fail
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [isAuthenticated, navigate]);

    if (loading) {
        return (
            <div className="placement-history-page">
                <div className="history-loading">
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    if (entries.length === 0) {
        return (
            <div className="placement-history-page">
                <div className="history-empty">
                    <h1>{t('history.title')}</h1>
                    <p>{t('history.empty')}</p>
                    <button className="primary-btn" onClick={() => navigate('/placement-test')}>
                        {t('history.startTest')}
                    </button>
                </div>
            </div>
        );
    }

    const latest = entries[0];
    const previous = entries.slice(1);

    // Compute level change from previous
    const getLevelChange = (current: CEFRLevel, prev: CEFRLevel | null): { text: string; direction: 'up' | 'down' | 'same' } | null => {
        if (!prev) return null;
        const ci = CEFR_ORDER.indexOf(current);
        const pi = CEFR_ORDER.indexOf(prev);
        if (ci > pi) return { text: `${prev} → ${current}`, direction: 'up' };
        if (ci < pi) return { text: `${prev} → ${current}`, direction: 'down' };
        return { text: '', direction: 'same' };
    };

    const prevLevel = previous.length > 0 ? previous[0].primaryLevel : null;
    const change = getLevelChange(latest.primaryLevel, prevLevel);

    // Chart data: reversed so oldest is on the left
    const chartData = entries.slice().reverse();

    // Compute chart Y range: only span the levels that actually appear (with 1 level padding)
    const levelIndices = chartData.map(e => CEFR_ORDER.indexOf(e.primaryLevel));
    const minIdx = Math.max(0, Math.min(...levelIndices) - 1);
    const maxIdx = Math.min(5, Math.max(...levelIndices) + 1);
    const yLabels = CEFR_ORDER.slice(minIdx, maxIdx + 1);
    const yRange = maxIdx - minIdx;

    // Build SVG path + points
    // Add 8% padding on each side so edge dots aren't clipped
    const xPadding = 8;
    const chartPoints = chartData.map((entry, i) => {
        const rawX = chartData.length === 1 ? 50 : (i / (chartData.length - 1)) * 100;
        const xPct = xPadding + (rawX / 100) * (100 - 2 * xPadding);
        const levelIdx = CEFR_ORDER.indexOf(entry.primaryLevel);
        // Add 10% vertical padding so dots at top/bottom aren't clipped
        const yPadding = 10;
        const rawY = yRange === 0 ? 50 : ((levelIdx - minIdx) / yRange) * 100;
        const yPct = yPadding + (rawY / 100) * (100 - 2 * yPadding);
        return { x: xPct, y: 100 - yPct, entry };
    });

    return (
        <div className="placement-history-page">
            <div className="history-container">
                <div className="history-header">
                    <button className="history-back-btn" onClick={() => navigate('/placement-test')}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        {t('history.back')}
                    </button>
                    <h1>{t('history.title')}</h1>
                </div>

                {/* Timeline visualization */}
                {entries.length >= 2 && (
                    <div className="history-timeline">
                        <div className="timeline-chart">
                            {/* Y-axis labels */}
                            <div className="timeline-y-labels">
                                {yLabels.map((level, i) => {
                                    const yPadding = 10;
                                    const rawPct = yRange === 0 ? 50 : (i / yRange) * 100;
                                    const adjustedPct = yPadding + (rawPct / 100) * (100 - 2 * yPadding);
                                    return (
                                        <span
                                            key={level}
                                            className="timeline-y-label"
                                            style={{ bottom: `${adjustedPct}%` }}
                                        >
                                            {level}
                                        </span>
                                    );
                                })}
                            </div>

                            {/* Chart area */}
                            <div className="timeline-chart-area">
                                {/* Grid lines + connecting line in SVG */}
                                <svg className="timeline-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                                    {yLabels.map((_, i) => {
                                        const yPadding = 10;
                                        const rawPct = yRange === 0 ? 50 : (i / yRange) * 100;
                                        const y = 100 - (yPadding + (rawPct / 100) * (100 - 2 * yPadding));
                                        return (
                                            <line key={i} x1="0" y1={y} x2="100" y2={y}
                                                stroke="var(--color-border)" strokeWidth="0.5" strokeDasharray="2,2"
                                                vectorEffect="non-scaling-stroke" />
                                        );
                                    })}
                                    {chartPoints.length >= 2 && (
                                        <polyline
                                            points={chartPoints.map(p => `${p.x},${p.y}`).join(' ')}
                                            fill="none"
                                            stroke="#667eea"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    )}
                                </svg>
                                {/* Dots as HTML elements (won't get stretched) */}
                                {chartPoints.map(p => (
                                    <div
                                        key={p.entry.sessionId}
                                        className="timeline-dot"
                                        style={{
                                            left: `${p.x}%`,
                                            top: `${p.y}%`,
                                            backgroundColor: getLevelColor(p.entry.primaryLevel),
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Latest result */}
                <div className="history-latest">
                    <span className="history-section-label">{t('history.latest')}</span>
                    <div
                        className="history-card history-card--latest history-card--clickable"
                        onClick={() => navigate(`/placement-test/result/${latest.sessionId}`)}
                    >
                        <div className="history-card-level" style={{ backgroundColor: getLevelColor(latest.primaryLevel) }}>
                            {latest.primaryLevel}
                        </div>
                        <div className="history-card-info">
                            <div className="history-card-date">{formatDate(latest.createdAt, i18n.language)}</div>
                            <div className="history-card-meta">
                                {latest.correctCount}/10 · {formatTime(latest.totalTimeSeconds)}
                            </div>
                            {change && change.direction !== 'same' && (
                                <div className={`history-card-change history-card-change--${change.direction}`}>
                                    {change.direction === 'up' ? '↑' : '↓'} {change.text}
                                </div>
                            )}
                            {change && change.direction === 'same' && (
                                <div className="history-card-change history-card-change--same">
                                    {t('history.same')}
                                </div>
                            )}
                        </div>
                        <svg className="history-card-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </div>
                </div>

                {/* Previous results */}
                {previous.length > 0 && (
                    <div className="history-previous">
                        <span className="history-section-label">{t('history.previous')}</span>
                        {previous.map((entry, idx) => {
                            const prevEntry = previous[idx + 1];
                            const entryChange = prevEntry ? getLevelChange(entry.primaryLevel, prevEntry.primaryLevel) : null;

                            return (
                                <div
                                    key={entry.sessionId}
                                    className="history-card history-card--clickable"
                                    onClick={() => navigate(`/placement-test/result/${entry.sessionId}`)}
                                >
                                    <div className="history-card-level" style={{ backgroundColor: getLevelColor(entry.primaryLevel) }}>
                                        {entry.primaryLevel}
                                    </div>
                                    <div className="history-card-info">
                                        <div className="history-card-date">{formatDate(entry.createdAt, i18n.language)}</div>
                                        <div className="history-card-meta">
                                            {entry.correctCount}/10 · {formatTime(entry.totalTimeSeconds)}
                                        </div>
                                        {entryChange && entryChange.direction !== 'same' && (
                                            <div className={`history-card-change history-card-change--${entryChange.direction}`}>
                                                {entryChange.direction === 'up' ? '↑' : '↓'} {entryChange.text}
                                            </div>
                                        )}
                                    </div>
                                    <svg className="history-card-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Retake CTA */}
                <div className="history-actions">
                    <button className="primary-btn" onClick={() => navigate('/placement-test')}>
                        {t('result.retakeTest')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PlacementTestHistoryPage;
